import { Box, Center, Divider, FocusTrap, Group, Modal, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedUniqueValues,
	useTable,
} from '@tanstack/react-table';
import type { SortingState } from '@tanstack/table-core';
import type { AxiosProgressEvent } from 'axios';
import { useCallback, useMemo, useState } from 'react';

import { AddIcon, DownloadIcon, ExportIcon, Upload } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { usePersistentColumnOrder } from '@apple/ui/data-table/hooks/use-persistent-column-order';
import { useTableDownload } from '@apple/ui/data-table/hooks/use-table-download';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { ExcelUploadDialog } from '@apple/utils/files/components/ExcelUploadDialog';
import { useFileDownload } from '@apple/utils/files/hooks/useFileDownload';
import { FormattedBoolean } from '@apple/utils/globalization/components/FormattedBoolean';
import { globalizationQueryOptions } from '@apple/utils/globalization/queries';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { marketQueries } from '@apple/features/market/queries';
import { plantQueryOptions } from '@apple/features/plants/queries/queries';
import { categoryQueries } from '@apple/features/product/queries/category';
import { productQueryKeys } from '@apple/features/product/queries/management';
import {
	archiveProducts,
	getProductManagementTemplateUrl,
	saveProducts,
	uploadProducts,
} from '@apple/features/product-retail/api/management';
import { PropagationConfirmation } from '@apple/features/product-retail/components/store-events/PropagationConfirmation';
import { usePropagationConfirmation } from '@apple/features/product-retail/hooks/usePropagationConfirmation';
import type { ProductFilters, ProductListing } from '@apple/features/product-retail/models/listing';
import type { AppleRetailProduct } from '@apple/features/product-retail/models/management';
import { getProductDataTableQueryOptions } from '@apple/features/product-retail/queries/odata';
import { propagateChanges } from '@apple/features/store-events/api/change-propagation';
import type { PropagationCandidate } from '@apple/features/store-events/models/change-propagation';
import { storeEventQueries } from '@apple/features/store-events/queries';

const features = createDataTableFeatures({
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/products')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductManagement.Read',
		});
	},
	component: ProductListRoute,
});

const defaultFilters: ProductFilters = {
	isActive: true,
	isEnabledInErp: true,
	isEndOfLifeComplete: false,
	isArchived: false,
};

const defaultSorting: SortingState = [{ id: 'name', desc: false }];

function ProductListRoute() {
	const { t } = useTranslation('manage');
	const [uploadOpen, uploadActions] = useDisclosure(false);
	const excelTemplate = useFileDownload({
		method: 'get',
		url: getProductManagementTemplateUrl(),
		fallbackFilename: 'products.xlsx',
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});
	const { queryClient } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const categoryQuery = useQuery(categoryQueries.all);

	const [eventPropagations, setEventPropagations] = useState<PropagationCandidate[]>([]);
	const tableState = useTableState<ProductListing, ProductFilters>({
		search,
		navigate,
		defaultFilters,
		defaultSorting,
		fieldMap: [],
	});

	const [productQuery, plantsQuery, countriesQuery, marketsQuery, categoriesQuery] = useQueries({
		queries: [
			getProductDataTableQueryOptions<ProductFilters>({
				pagination: tableState.state.pagination,
				filters: tableState.currentFilterData,
				sorting: tableState.state.sorting,
			}),
			plantQueryOptions.plants,
			globalizationQueryOptions.countriesWithCultures(true),
			marketQueries.getMarkets(),
			storeEventQueries.categories,
		],
	});

	const columnHelper = createDataTableColumnHelper<ProductListing, ProductFilters>();

	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.accessor('sku', {
					id: 'partNumber',
					enableColumnFilter: true,
					header: t('products.fields.sku'),
					filter: {
						group: t('products.filterGroups.productSearch'),
						sortOrder: 2,
					},
					size: 70,
					cell: ({ row }) => (
						<Link
							to='/manage/products/$itemId'
							state={{ from: location }}
							params={{ itemId: row.original.itemId }}
							search={{ copy: undefined }}
							title={t('products.links.view')}
						>
							<Text size='sm'>{row.original.sku}</Text>
						</Link>
					),
				}),
				columnHelper.accessor('productName', {
					id: 'name',
					enableResizing: true,
					header: t('products.fields.productName'),
					enableColumnFilter: true,
					filter: {
						group: t('products.filterGroups.productSearch'),
						sortOrder: 1,
					},
					size: 150,
					cell: ({ row }) => (
						<Box
							style={{
								width: '250px',
								display: 'block',
								overflow: 'hidden',
								whiteSpace: 'nowrap',
								textOverflow: 'ellipsis',
							}}
							title={row.original.productName}
						>
							{row.original.productName}
						</Box>
					),
				}),
				columnHelper.filterOnly('categoryIds', {
					group: t('products.filterGroups.productSearch'),
					label: t('products.filters.category'),
					getFilterDisplayValue: value =>
						categoryQuery.data?.find(x => x.categoryId === Number(value))
							?.description ?? String(value),
					variant: 'multi-select',
				}),
				columnHelper.accessor('warehouseCode', {
					id: 'warehouseCode',
					header: t('products.fields.warehouseCode'),
					enableColumnFilter: true,
					size: 50,
					filter: {
						group: t('products.filterGroups.availability'),
						variant: 'select',
						sortOrder: 6,
						getFilterDisplayValue: value => {
							const plant = plantsQuery.data?.find(x => x.warehouseCode === value);
							return plant ? `${plant.name} (${plant.warehouseCode})` : String(value);
						},
					},
					// TODO: Replace with remote data
					// getUniqueValues: () => ['N800', 'E800', 'A804', 'A801', '777'],
				}),
				columnHelper.accessor('availableQuantity', {
					id: 'availableQuantity',
					header: t('products.fields.atp'),
					enableColumnFilter: false,
					size: 50,
				}),
				columnHelper.accessor('isActive', {
					id: 'isActive',
					header: t('products.fields.isActive'),
					size: 70,
					enableSorting: false,
					filter: {
						group: t('products.filterGroups.availability'),
						variant: 'switch',
						sortOrder: 3,
					},
					cell: ({ row }) => (
						<Center>
							<FormattedBoolean size='sm' value={row.original.isActive} useIcon />
						</Center>
					),
				}),
				columnHelper.accessor('isAdministratorOnly', {
					id: 'isAdministratorOnly',
					header: t('products.fields.visibility'),
					enableColumnFilter: true,
					filter: {
						group: t('products.filterGroups.availability'),
						variant: 'segmented-switch',
						sortOrder: 7,
						getFilterDisplayValue: x => {
							switch (x) {
								case true:
									return t('product:details.visibility.confidential');
								case false:
									return t('product:details.visibility.public');
								default:
									return t('common:label.all');
							}
						},
					},
					cell: ({ row }) => (
						<Text size='sm'>
							{row.original.isAdministratorOnly
								? t('product:details.visibility.confidential')
								: t('product:details.visibility.public')}
						</Text>
					),
				}),
				columnHelper.accessor('isBackorderable', {
					id: 'isBackorderable',
					header: t('products.fields.backorderable'),
					filter: {
						group: t('products.filterGroups.availability'),
						variant: 'false-only-switch',
						sortOrder: 5,
						label: t('products.filters.notBackorderable'),
					},
					size: 50,
					enableColumnFilter: true,
					cell: ({ row }) => (
						<Group justify='center'>
							<FormattedBoolean
								size='sm'
								value={row.original.isBackorderable}
								useIcon
							/>
						</Group>
					),
				}),
				columnHelper.accessor('availableQuantity', {
					id: 'availability',
					header: t('products.filters.availability.label'),
					meta: {
						hideColumn: true,
					},
					enableColumnFilter: true,
					filter: {
						group: t('products.filterGroups.availability'),
						variant: 'segmented-switch',
						sortOrder: 7,
						getFilterDisplayValue: x => {
							switch (x) {
								case 'inStock':
									return t('products.filters.availability.inStock');
								case 'outOfStock':
									return t('products.filters.availability.outOfStock');
								default:
									return t('common:label.all');
							}
						},
					},
				}),
				columnHelper.accessor('requiresApproval', {
					id: 'requiresApproval',
					header: t('products.fields.approvalRequired'),
					size: 50,
					filter: {
						group: t('products.filterGroups.approvals'),
						variant: 'switch',
					},
					cell: ({ row }) => (
						<Center>
							<FormattedBoolean
								size='sm'
								value={row.original.requiresApproval}
								useIcon
							/>
						</Center>
					),
				}),
				columnHelper.filterOnly('isEndOfLifeComplete', {
					group: t('products.filterGroups.availability'),
					label: t('products.fields.isEndOfLifeComplete'),
					variant: 'switch',
				}),
				columnHelper.filterOnly('isEnabledInErp', {
					group: t('products.filterGroups.availability'),
					label: t('products.fields.isEnabledErp'),
					variant: 'switch',
				}),
				columnHelper.filterOnly('hasImages', {
					group: t('products.filterGroups.availability'),
					label: t('products.filters.missingImages'),
					variant: 'false-only-switch',
				}),
				columnHelper.filterOnly('isArchived', {
					group: t('products.filterGroups.availability'),
					label: t('products.filters.archived'),
					variant: 'switch',
				}),
				columnHelper.downloadOnly({
					accessorKey: 'eolType',
					header: t('products.fields.eolType'),
				}),
				columnHelper.downloadOnly({
					accessorKey: 'isEndOfLifeComplete',
					header: t('products.fields.endOfLifeStatus'),
				}),
				columnHelper.downloadOnly({
					accessorKey: 'replacementSku',
					header: t('products.fields.replacementSku'),
				}),
				columnHelper.downloadOnly({
					accessorKey: 'unitOfMeasure',
					header: t('products.fields.unitOfMeasure'),
				}),
				columnHelper.downloadOnly({
					accessorKey: 'commercialPrice',
					header: t('products.fields.commercialPrice'),
				}),
				columnHelper.downloadOnly({
					accessorKey: 'currencyCode',
					header: t('products.fields.currencyCode'),
				}),
			]),
		[t, plantsQuery.data, location, columnHelper, categoryQuery.data],
	);

	const { columnOrder, handleSetColumnOrder } = usePersistentColumnOrder(
		'manage:products:column-order',
		columns,
	);

	const table = useTable({
		features,
		data: productQuery.data.rows,
		rowCount: productQuery.data.totalRowCount,
		columns,
		...tableState,
		state: {
			...tableState.state,
			columnOrder,
		},
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		meta: {
			getFacetedMinMaxValues: columnId => {
				switch (columnId) {
					case 'availableQuantity':
						return [0, 100];
					default:
						return undefined;
				}
			},
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'warehouseCode':
						return new Map(plantsQuery.data?.map(x => [x.warehouseCode, 1]));
					case 'availability':
						return new Map([undefined, 'inStock', 'outOfStock'].map(x => [x, 1]));
					case 'hasQuantityLimit':
						return new Map([undefined, 'yes', 'no'].map(x => [x, 1]));
					case 'isAdministratorOnly':
						return new Map([undefined, true, false].map(x => [x, 1]));
					case 'categoryIds':
						return new Map(categoryQuery.data?.map(x => [x.categoryId, 1]));
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
	});

	const tableDownload = useTableDownload({
		table,
		queryOptionsFn: getProductDataTableQueryOptions,
	});

	const handleConfirmPropagations = useCallback((confirmed: PropagationCandidate[]) => {
		setEventPropagations(confirmed);
	}, []);
	const {
		opened: propagationsOpened,
		candidates,
		handleBulkProductUpload,
		handleConfirm,
	} = usePropagationConfirmation({ onConfirm: handleConfirmPropagations });

	const handleExport = useCallback(() => {
		void tableDownload.download({
			filters: tableState.currentFilterData,
			fileName: 'Products',
		});
	}, [tableDownload, tableState.currentFilterData]);

	const handleUpload = useCallback(
		async (file: File, onUploadProgress?: (event: AxiosProgressEvent) => void) => {
			const results = await uploadProducts(file, onUploadProgress);
			await handleBulkProductUpload(results);
			return results;
		},
		[handleBulkProductUpload],
	);

	const handleUploadConfirmed = useCallback(async () => {
		if (eventPropagations.length > 0) {
			await propagateChanges(eventPropagations, 'N/A');
		}
		await Promise.all([
			queryClient.resetQueries({
				queryKey: productQueryKeys.all,
			}),
			queryClient.resetQueries({
				queryKey: productQueryKeys.unassignedSkus,
			}),
		]);
		await productQuery.refetch();
	}, [eventPropagations, productQuery, queryClient]);

	return (
		<TableLayout
			title={t('products.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('products.buttons.add.tooltip')}
					icon={AddIcon}
					onClick={() => {
						void navigate({ to: '/manage/products/add', state: { from: location } });
					}}
					data-testid='add-product'
				/>,
				<Divider key='download-divider' orientation='vertical' mx='xs' />,
				<ToolbarButton
					key='download'
					tooltip={t('products.buttons.download.tooltip')}
					icon={DownloadIcon}
					loading={excelTemplate.downloading}
					onClick={() => void excelTemplate.download()}
				/>,
				<ToolbarButton
					key='upload'
					tooltip={t('products.buttons.upload.tooltip')}
					icon={Upload}
					disabled={uploadOpen}
					onClick={uploadActions.open}
					data-testid='upload-bulk-template'
				/>,
				<Divider key='export-divider' orientation='vertical' mx='xs' />,
				<ToolbarButton
					key='export'
					loading={tableDownload.downloading}
					tooltip={t('products.buttons.export.tooltip')}
					icon={ExportIcon}
					onClick={handleExport}
				/>,
			]}
		>
			<DataTable
				enableColumnReordering
				columnOrder={columnOrder}
				onColumnOrderChange={handleSetColumnOrder}
				table={table}
				variant='apple-table'
				loading={productQuery.isFetching}
			/>
			<ExcelUploadDialog<AppleRetailProduct, 'itemId'>
				keyProperty={'itemId'}
				keyLabel={t('products.fields.sku')}
				opened={uploadOpen}
				close={uploadActions.close}
				onUpload={handleUpload}
				onSave={products => saveProducts(products, 'N/A')}
				onDelete={itemIds => archiveProducts(itemIds, 'N/A')}
				onConfirmed={handleUploadConfirmed}
				onReset={() => setEventPropagations([])}
			/>
			<Modal
				id='propagation-dialog'
				title={t('store-event:changePropagation.title')}
				opened={propagationsOpened}
				onClose={() => {
					// NOOP
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
				withCloseButton={false}
				size='xl'
			>
				<FocusTrap.InitialFocus />
				<PropagationConfirmation
					candidates={candidates}
					onConfirm={handleConfirm}
					alwaysShowItemId
					plants={plantsQuery.data}
					countries={countriesQuery.data}
					markets={marketsQuery.data}
					categories={categoriesQuery.data}
					groupStoresWithinItem={true}
					showAssociationDetails={true}
				/>
			</Modal>
		</TableLayout>
	);
}
